import TypeColor from 'color'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation, WithTranslation } from 'react-i18next'
import { FiCheck, FiExternalLink, FiHome, FiMapPin } from 'react-icons/fi'
import Flex from 'typescript-styled-flex'
import { useDispatch, useSelector } from 'react-redux'
import GoogleMapReact from 'google-map-react'
import { formValues } from 'redux-form'
import CalendarImportIcon from '../../../assets/integrations/icalendar-arrow-left.svg'
import CalendarExportIcon from '../../../assets/integrations/icalendar-arrow-right.svg'
import CalendarSyncIcon from '../../../assets/integrations/icalendar-sync.svg'
import { Color } from '../../../components/ColorPalette'
import {
  ColorInput,
  Form,
  FormGroup,
  NumberInput,
  Select,
  TextareaInput,
  TextInput,
} from '../../../components/FormBuilder'
import {
  Desktop,
  is,
  isTouchDevice,
  Mobile,
} from '../../../components/MediaQuery'
import { Label, Wrapper } from '../../../components/Template/Links'
import styled, { theme } from '../../../theme'
import useSize from '../../../hooks/useSize'
import Flag from '../../../components/Template/Flag'
import {
  getCountryDict,
  getUserObjectId,
} from '../../../reducers/userObject/selectors/object'
import Button from '../../../components/Template/Button'
import RoomTypes from './RoomTypes'
import { IFormValues } from '.'
import { IStore } from '../../../reducers'
import { FacilitiesActions } from '../../../reducers/facilities/actionNames'
import { Visibility } from '@material-ui/icons'
import Tooltip from '../../../components/Tooltip/Tooltip'
import { groupBy } from 'lodash'
import {
  getUserLanguage,
  getUserPriceGroup,
  isUserNewPremium,
} from '../../../reducers/userAccount/selectors/userAccount'
import { getRoomModalIsSaving } from '../../../reducers/bookingCalendar/selectors/getModalOpen'
import { fireToastWarning } from '../../../actions/toasts/fireToast'
import i18n from '../../../i18n'
import rotate from '../../../actions/toasts/icons/rotate'
import {
  isAllInOneLicenseValid,
  isLicenseValid,
} from '../../../reducers/userAccount/selectors/license'
import {
  isInPlanScope,
  PlansScope,
  useLicensePlansScope,
} from '../../Template/Sidebar/Menu'
import { PopoveredIcon2 } from '../../../components/PopoveredIcon/PopoveredIcon'
import UpgradePremiumBlockScreenModal from '../../UpgradePremiumBlockScreenModal/UpgradePremiumBlockScreenModal'
import { useMediaQuery, useTheme } from '@material-ui/core'

const GEOCODING_API_KEY =
  process.env.REACT_APP_GEOCODING_KEY ||
  'AIzaSyCqj555GrJmY9jNdgZrRbT5ipoemoj28HA'

const newFacilitiesIds = [81, 82, 83, 84, 85, 86, 87, 88, 89, 90, 91, 92, 93]
const newFacilitiesGroups = ['ecology', 'remote work']

function numberCheck(value, previousValue) {
  const re = /^[0-9\b]+$/
  if (value === '' || re.test(value)) {
    return value
  } else {
    return previousValue
  }
}

const Link = styled.a`
  font-family: Montserrat;
  font-size: 12px;
  color: #17a3dd;
  text-decoration: none solid rgb(23, 163, 221);
  text-align: right;
`

function RoomEditor({
  t,
  syncFits,
  ...props
}: IRoomEditorProps & WithTranslation) {
  const isDesktop = is.desktop()
  const size = useSize()
  const facilities = useSelector((state: IStore) => state.facilities.facilities)
  const facilitiesObject = useSelector(
    (state: IStore) => state.facilities.facilitiesObject,
  )
  const objectId = useSelector(getUserObjectId)
  const dispatch = useDispatch()
  const isSaving = useSelector(getRoomModalIsSaving)
  const planScope = useLicensePlansScope()
  const userPriceGroup = useSelector(getUserPriceGroup)
  const isLiteUser = isInPlanScope(
    planScope,
    PlansScope.ContactDetails,
    userPriceGroup,
  )
  const isUserLicenceValid = useSelector(isLicenseValid)
  const isAllInOnePriceGroup = useSelector(isUserNewPremium)
  const isAllInOneUser = useSelector(isAllInOneLicenseValid)
  const isAllInOneFreeUser = !isAllInOneUser && isAllInOnePriceGroup
  const theme2 = useTheme()
  const isMobile = useMediaQuery(theme2.breakpoints.down('sm'))
  const [isModalOpened, setIsModalOpened] = useState(false)

  useEffect(() => {
    if (syncFits) {
      dispatch(
        fireToastWarning(
          i18n.t('OUT_4858_toast_booking_com_room_size_error_2'),
          null,
          'Main',
          'recalculateRoomFits',
          true,
          i18n.t('OUT_4858_toast_booking_com_room_size_error_1'),
          () => {
            onCalculate()
          },
          rotate(),
        ),
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncFits])

  useEffect(() => {
    dispatch({ type: FacilitiesActions.LoadFacilities })
    dispatch({ type: FacilitiesActions.LoadObjectFacilities })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectId])

  const [roomFacilities, setRoomFacilities] = useState([])

  useEffect(() => {
    const keys = []

    Object.entries(facilitiesObject).forEach(([key, value]) => {
      if (value.includes(String(props.roomId)) || value.includes('0')) {
        keys.push(key)
      }
    })

    if (facilities) {
      setRoomFacilities(
        keys.map(key =>
          Object.values(facilities)
            .reduce((a, b) => a.concat(b), [])
            .find((facility: any) => String(facility.id) === String(key)),
        ),
      )
    }
  }, [props.roomId, facilitiesObject, facilities])

  const groupped = groupBy(roomFacilities, 'category')

  const countryDictionary = useSelector(getCountryDict)

  const [popoverAnchor, setPopoverAnchor] = useState(null)

  const handlePopoverOpen = event => {
    event.preventDefault()
    event.stopPropagation()
    setPopoverAnchor(event.currentTarget)
  }

  const handlePopoverClose = event => {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    setPopoverAnchor(null)
  }

  const onCalculate = () => {
    dispatch((props as any).change('number_of_people', Number(syncFits)))
    dispatch((props as any).change('roomSize', Number(syncFits)))
    dispatch((props as any).change('capacity', Number(syncFits)))

    dispatch((props as any).change('dontClose', true))

    setTimeout(() => {
      dispatch((props as any).submit())
    }, 300)
  }

  function optionWithFlags(country) {
    const flag = (
      <Flag countryCode={country.code.toLowerCase()} width={16} height={16} />
    )

    return (
      <Select.Option
        data-cy={`room-form-country-${country.name}-select-option`}
        key={country.id}
        value={Number(country.id)}
        icon={flag}
      >
        {country.name}
      </Select.Option>
    )
  }


  return (
    <Flex full={true}>
      <Form onSubmit={null} column={!isDesktop} row={isDesktop}>
        <Flex
          column={true}
          style={
            isDesktop
              ? { minWidth: 0, paddingRight: `${20 * size}px`, width: '100%' }
              : { minWidth: 0 }
          }
        >
          <FormGroup.Render
            size={size}
            column={true}
            title={t('basic_information')}
          >
            <Flex
              column={!isDesktop}
              row={isDesktop}
              justifyBetween={isDesktop}
            >
              <TextInput.ReduxField
                size={size}
                name={'name'}
                label={t('room_name')}
                width={isDesktop ? `calc(50% - ${10 * size}px)` : '100%'}
                className="room-input-name"
                data-cy={'room-form-name-text-field'}
              />
              <RoomTypes
                size={size}
                width={isDesktop ? `calc(50% - ${10 * size}px)` : '100%'}
              />
            </Flex>
            <TextareaInput.ReduxField
              size={size}
              name={'description'}
              label={t('room_description')}
              maxLength={500}
              width={'100%'}
              style={{ resize: 'none' }}
              className={'room-input-description'}
              data-cy={'room-form-description-text-area'}
            />
            <TextareaInput.ReduxField
              size={size}
              wrapperStyle={{ marginTop: '0' }}
              name={'additionalEquipment'}
              label={t('BBO_1198_equipment')}
              maxLength={500}
              width={'100%'}
              style={{ resize: 'none' }}
              className={'room-input-description'}
              data-cy={'room-form-additional-equipment-text-area'}
            />
            <NumberInput.ReduxField
              wrapperStyle={{ marginTop: '0' }}
              size={size}
              name={'size'}
              label={t('BBO_1198_dimension')}
              width={'100%'}
              withoutPicker={true}
              decimal={2}
              minValueForNumber={0}
              maxValueForNumber={9999}
            />
            {props.roomId && (
              <FormGroup.Render
                size={size}
                column={true}
                title={t('BBO_3139_facilities')}
              >
                <div>
                  <div
                    style={{
                      color: '#696a6d',
                      fontSize: '12px',
                      display: 'flex',
                    }}
                  >
                    {roomFacilities.length === 0 ? (
                      t('BBO_3899_none')
                    ) : (
                      <Tooltip
                        size={size}
                        placement={'right'}
                        title={
                          <div>
                            {Object.entries(groupped).map(([key, value]) => (
                              <div key={key} style={{ display: 'flex' }}>
                                <div
                                  style={{
                                    fontWeight: 'bold',
                                    marginBottom: '5px',
                                    marginRight: '5px',
                                  }}
                                >
                                  {t(
                                    (newFacilitiesGroups.includes(key)
                                      ? 'BED_11593_'
                                      : 'BBO_3139_') +
                                      key.toLowerCase().replace(/ /g, '_'),
                                  )}
                                </div>
                                {value.map(
                                  (f, i: any) =>
                                    t(
                                      (newFacilitiesIds.includes(Number(f.id))
                                        ? 'BED_11593_'
                                        : 'BBO_3139_') +
                                        f.name.toLowerCase().replace(/ /g, '_'),
                                    ) + (value.length !== i + 1 ? ', ' : ''),
                                )}
                              </div>
                            ))}
                          </div>
                        }
                        disableHoverListener={isTouchDevice}
                        isRoom={true}
                      >
                        <div
                          style={{
                            display: 'flex',
                            overflow: 'hidden',
                            justifyContent: 'space-between',
                            width: '100%',
                          }}
                        >
                          <div
                            style={{
                              textOverflow: 'ellipsis',
                              overflow: 'hidden',
                              whiteSpace: 'nowrap',
                              width: '100%',
                            }}
                          >
                            {roomFacilities.map((f, i) => {
                              return (
                                t(
                                  (newFacilitiesIds.includes(Number(f.id))
                                    ? 'BED_11593_'
                                    : 'BBO_3139_') +
                                    f.name.toLowerCase().replace(/ /g, '_'),
                                ) +
                                (roomFacilities.length !== i + 1 ? ', ' : '')
                              )
                            })}
                          </div>
                          <div>({roomFacilities.length})</div>
                          <div style={{ marginLeft: '4px' }}>
                            <Visibility
                              style={{
                                fontSize: '14px',
                              }}
                            />
                          </div>
                        </div>
                      </Tooltip>
                    )}
                  </div>
                </div>
                {(!isUserLicenceValid || isLiteUser) && !isAllInOneFreeUser ? <div style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  gap: 5
                }}>
                  <p onClick={isMobile ? () => setIsModalOpened(true) : handlePopoverOpen} style={{
                    fontSize: 11,
                    color: '#A1A1A1',
                    fontFamily: 'Montserrat',
                    fontWeight: 500
                  }}>{t('title_activity_settings')}</p>
                  <PopoveredIcon2 anchorEl={popoverAnchor} onClose={handlePopoverClose} />
                  {isMobile && <UpgradePremiumBlockScreenModal
                    open={isModalOpened}
                    close={() => setIsModalOpened(false)}
                  />}
                </div> : <Link target={'_blank'} href={'/offer/facilities'}>
                  {t('title_activity_settings')}
                  <FiExternalLink />
                </Link>}
              </FormGroup.Render>
            )}
            <FormGroup.Render
              size={size}
              title={t('BBO_1743_sharing_offer')}
              column={false}
              row={true}
              withPopoverIcon={(!isUserLicenceValid || isLiteUser) && !isAllInOneFreeUser}
              style={{
                marginBottom: `${15 * size}px`,
                marginTop: '15px',
                display: 'flex',
                gap: '8px',
                alignItems: 'center',
              }}
            >
              <Select.ReduxField
                size={size}
                name={'widget'}
                menuPlacement={'auto'}
                width={'100%'}
                placeholder={t('select')}
                className={'room-input-select-position'}
                data-cy={'room-form-widget-select'}
                disabled={(!isUserLicenceValid || isLiteUser) && !isAllInOneFreeUser}
                withPopoverIcon={(!isUserLicenceValid || isLiteUser) && !isAllInOneFreeUser}
              >
                <Select.Option
                  data-cy={'room-form-widget-yes-select-option'}
                  value={1}
                >
                  {t('yes')}
                </Select.Option>
                <Select.Option
                  data-cy={'room-form-widget-no-select-option'}
                  value={0}
                >
                  {t('no')}
                </Select.Option>
              </Select.ReduxField>
            </FormGroup.Render>
            <FormGroup.Render
              size={size}
              title={t('BBO_2878_offer_title')}
              column={false}
              row={true}
              style={{
                marginBottom: `${15 * size}px`,
                display: 'flex',
                gap: '8px',
                alignItems: 'center',
              }}
              withPopoverIcon={(!isUserLicenceValid || isLiteUser) && !isAllInOneFreeUser}
            >
              <TextInput.ReduxField
                size={size}
                name={'title'}
                width={'100%'}
                maxLength={64}
                className="room-input-name"
                withPopoverIcon={(!isUserLicenceValid || isLiteUser) && !isAllInOneFreeUser}
                disabled={(!isUserLicenceValid || isLiteUser) && !isAllInOneFreeUser}
              />
            </FormGroup.Render>
          </FormGroup.Render>
        </Flex>
        <Flex
          column={true}
          style={
            isDesktop ? { paddingRight: `${20 * size}px`, width: '100%' } : {}
          }
        >
          <FormGroup.Render
            size={size}
            title={t('people')}
            column={false}
            row={true}
          >
            <Flex style={{ width: '100%' }} alignEnd={true}>
              <NumberInput.ReduxField
                name={'roomSize'}
                size={size}
                minValueForNumber={1}
                maxValueForNumber={16}
                label={t('number_of_people')}
                normalize={numberCheck}
                disabled={props.booking && syncFits === false}
                disabledInput={props.booking && syncFits !== false}
                warningMsg={
                  props.booking && syncFits !== false ? ' ' : undefined
                }
                isCalculating={isSaving}
                withoutPicker={props.booking}
                onCalculate={
                  props.booking && syncFits !== false ? onCalculate : undefined
                }
                onRefresh={() => {}}
                className={'room-input-room-size'}
                wrapperStyle={{ marginRight: `${5 * size}px` }}
              />
              <NumberInput.ReduxField
                name={'additionalBeds'}
                label={t('room_reservation_additional_bed')}
                minValueForNumber={0}
                size={size}
                maxValueForNumber={4}
                normalize={numberCheck}
                className={'room-input-additional-beds'}
                wrapperStyle={{ marginLeft: `${5 * size}px` }}
              />
            </Flex>
          </FormGroup.Render>
          <FormGroup.Render
            title={t('number_of_beds')}
            column={false}
            row={true}
            size={size}
          >
            <Flex style={{ width: '100%' }} alignEnd={true}>
              <NumberInput.ReduxField
                name={'singleBed'}
                size={size}
                label={t('number_of_beds_single')}
                minValueForNumber={0}
                maxValueForNumber={16}
                normalize={numberCheck}
                className={'room-input-single-bed'}
                wrapperStyle={{ marginRight: `${5 * size}px` }}
              />
              <NumberInput.ReduxField
                name={'doubleBed'}
                size={size}
                label={t('number_of_beds_twin')}
                minValueForNumber={0}
                maxValueForNumber={8}
                normalize={numberCheck}
                className={'room-input-double-bed'}
                wrapperStyle={{ marginLeft: `${5 * size}px` }}
              />
            </Flex>
          </FormGroup.Render>
          {!props.newRoom && (
            <FormGroup.Render
              size={size}
              title={t('room_display_order')}
              column={false}
              row={true}
              style={{ marginBottom: `${15 * size}px` }}
            >
              <Select.ReduxField
                zIndex={102}
                name={'position'}
                menuPlacement={'auto'}
                size={size}
                width={'100%'}
                placeholder={t('select')}
                className={'room-input-select-position'}
                data-cy={'room-form-position-select'}
              >
                {props.positions.map((_, index) => (
                  <Select.Option
                    data-cy={`room-form-position${index + 1}-select-option`}
                    key={index}
                    value={index + 1}
                  >
                    {index + 1}
                  </Select.Option>
                ))}
              </Select.ReduxField>
            </FormGroup.Render>
          )}
          <Mobile>
            <div style={{ marginTop: `${15 * size}px` }}>
              <ColorInput.ReduxField
                width={'100%'}
                size={size}
                isHorizontal={true}
                name={'customColor'}
                colors={props.colors}
                colorLabel={t('mark_the_room_with_color')}
                isBoldLabel={true}
                className={'room-input-color'}
                noAlign={true}
                withPopoverIcon={(!isUserLicenceValid || isLiteUser) && !isAllInOneFreeUser}
                enableSideButton={true}
                disabled={(!isUserLicenceValid || isLiteUser) && !isAllInOneFreeUser}
              />
            </div>
          </Mobile>
          {props.type === 'update' &&
            (props.booking || props.iCalendar || props.airbnb) && (
              <FormGroup.Render
                size={size}
                title={t('z4502_integrations')}
                column={!isDesktop}
                row={isDesktop}
                justifyBetween={true}
              >
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: isDesktop ? '1fr 1fr' : '1fr',
                    width: '100%',
                    gap: '10px',
                  }}
                >
                  {props.airbnb && (
                    <Wrapper
                      style={{ width: '100%' }}
                      size={size}
                      className={'room-airbnb'}
                    >
                      <Color
                        size={size}
                        small={true}
                        color={theme.colors.palette.airbnb}
                        className={'room-airnbnb-color'}
                      />
                      <Label size={size}>Airbnb</Label>
                      <Color
                        size={size}
                        style={{ margin: 'auto', marginRight: `${7 * size}px` }}
                        small={true}
                        color={theme.colors.palette.airbnb}
                        className={'room-airnbnb-color'}
                      >
                        <FiCheck size={16 * size} />
                      </Color>
                    </Wrapper>
                  )}

                  {props.booking && (
                    <Wrapper
                      style={{ width: '100%' }}
                      size={size}
                      className={'room-booking'}
                    >
                      <Color
                        size={size}
                        small={true}
                        color={theme.colors.palette.booking}
                        className={'room-booking-color'}
                      />
                      <Label size={size}>Booking.com</Label>
                      <Color
                        size={size}
                        style={{ margin: 'auto', marginRight: `${7 * size}px` }}
                        small={true}
                        color={theme.colors.palette.booking}
                        className={'room-booking-color'}
                      >
                        <FiCheck size={16 * size} />
                      </Color>
                    </Wrapper>
                  )}

                  {props.iCalendar && (
                    <Wrapper
                      style={{ width: '100%' }}
                      size={size}
                      className={'room-icalendar'}
                    >
                      <Color
                        size={size}
                        small={true}
                        color={theme.colors.palette.icalendar}
                        className={'room-icalendar-color'}
                      />
                      <Label size={size}>iCalendar</Label>
                      <img
                        style={{ margin: 'auto', marginRight: '0px' }}
                        width={20 * size}
                        height={20 * size}
                        alt={''}
                        src={
                          props.iCalendarSync === 3
                            ? CalendarSyncIcon
                            : props.iCalendarSync === 1
                            ? CalendarImportIcon
                            : CalendarExportIcon
                        }
                      />
                      <Color
                        size={size}
                        small={true}
                        color={theme.colors.palette.icalendar}
                        className={'room-icalendar-color'}
                      >
                        <FiCheck size={16 * size} />
                      </Color>
                    </Wrapper>
                  )}
                </div>
              </FormGroup.Render>
            )}
          <Desktop>
            <div style={{ marginTop: `${15 * size}px` }}>
              <ColorInput.ReduxField
                size={size}
                alwaysExpanded={true}
                width={'70em'}
                isHorizontal={true}
                name={'customColor'}
                colors={props.colors}
                colorLabel={t('mark_the_room_with_color')}
                isBoldLabel={true}
                className={'room-input-color'}
                noAlign={true}
                withPopoverIcon={(!isUserLicenceValid || isLiteUser) && !isAllInOneFreeUser}
                disabled={(!isUserLicenceValid || isLiteUser) && !isAllInOneFreeUser}
              />
            </div>
          </Desktop>
        </Flex>
        <Flex column={true} style={{ width: '100%' }}>
          <FormGroup.Render
            size={size}
            title={t('BBO_3021_address_data')}
            column={true}
          >
            <Select.ReduxField
              size={size}
              name={'id_country'}
              label={t('profile_country')}
              width={'100%'}
              noOptionsMessage={t('search_not_found')}
              isSearchable={true}
              data-cy={'room-form-country-select'}
              className={'room-form-country-select'}
              disabled={!isUserLicenceValid && !isAllInOneFreeUser}
              withPopoverIcon={!isUserLicenceValid && !isAllInOneFreeUser}
              labelStyle={{
                display: 'flex',
                alignItems: 'baseline',
                gap: '8px',
              }}
            >
              {countryDictionary.map(country => optionWithFlags(country))}
            </Select.ReduxField>
            <TextInput.ReduxField
              size={size}
              name={'street'}
              label={t('reservation_person_street')}
              labelStyle={{
                display: 'flex',
                alignItems: 'baseline',
                gap: '8px',
              }}
              width={'100%'}
              disabled={!isUserLicenceValid && !isAllInOneFreeUser}
              withPopoverIcon={!isUserLicenceValid && !isAllInOneFreeUser}
            />
            <TextInput.ReduxField
              name={'post_code'}
              label={t('reservation_person_post_code')}
              width={'100%'}
              size={size}
              disabled={!isUserLicenceValid && !isAllInOneFreeUser}
              withPopoverIcon={!isUserLicenceValid && !isAllInOneFreeUser}
              labelStyle={{
                display: 'flex',
                alignItems: 'baseline',
                gap: '8px',
              }}
            />
            <TextInput.ReduxField
              size={size}
              name={'city'}
              label={t('reservation_person_town')}
              width={'100%'}
              disabled={!isUserLicenceValid && !isAllInOneFreeUser}
              withPopoverIcon={!isUserLicenceValid && !isAllInOneFreeUser}
              labelStyle={{
                display: 'flex',
                alignItems: 'baseline',
                gap: '8px',
              }}
            />
            <GeoCoding size={size} props={props} isAllInOneFreeUser={isAllInOneFreeUser}/>
          </FormGroup.Render>
        </Flex>
      </Form>
    </Flex>
  )
}

const GeoCoding = formValues<any>({
  city: 'city',
  idCountry: 'id_country',
  lat: 'coords_lat',
  lng: 'coords_lng',
  postCode: 'post_code',
  street: 'street',
})(({ size, idCountry, street, city, postCode, lat, lng, props,isAllInOneFreeUser }) => {
  const { t } = useTranslation()
  const mapRef = useRef()
  const mapsRef = useRef()
  const [error, setError] = useState('')
  const [bootstrap, setBootstrap] = useState(false)
  const isUserLicenceValid = useSelector(isLicenseValid)
  const theme2 = useTheme()
  const isMobile = useMediaQuery(theme2.breakpoints.down('sm'))
  const [isModalOpened, setIsModalOpened] = useState(false)
  const [popoverAnchor, setPopoverAnchor] = useState(null)
  const handlePopoverOpen = event => {
    event.preventDefault()
    event.stopPropagation()
    setPopoverAnchor(event.currentTarget)
  }

  const handlePopoverClose = event => {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    setPopoverAnchor(null)
  }

  const isDesktop = is.desktop()

  const countryDict = useSelector(getCountryDict)
  const country = countryDict.find(c => String(c.id) === String(idCountry))
  const countryName = country ? country.name : ''

  const userLang = useSelector(getUserLanguage)

  const onBlur = e => {
    if (e.target.value) {
      const latlng = e.target.value.split(',')
      if (latlng.length > 1) {
        props.change('coords_lat', latlng[0])
        props.change('coords_lng', latlng[1])
      }
    }
  }

  const geocode = maps => {
    const geocoder = new maps.Geocoder()
    geocoder.geocode(
      {
        address: `${street},+${city},+${postCode}+${countryName}`,
      },
      function(results, response) {
        switch (response) {
          case 'OK':
            const { lat, lng } = results[0].geometry.location
            props.change('coords_lat', lat())
            props.change('coords_lng', lng())
            props.change('googleMapGeocoding', `${lat()},${lng()}`)
            setError('')
            break
          case 'ZERO_RESULTS':
            props.change('coords_lat', '')
            props.change('coords_lng', '')
            props.change('googleMapGeocoding', '')
            setError('BBO_2858_zero_results')
            break
          case 'REQUEST_DENIED':
            props.change('coords_lat', '')
            props.change('coords_lng', '')
            props.change('googleMapGeocoding', '')
            setError('BBO_2858_REQUEST_DENIED')
            break
          case 'INVALID_REQUEST':
            props.change('coords_lat', '')
            props.change('coords_lng', '')
            props.change('googleMapGeocoding', '')
            setError('BBO_2858_INVALID_REQUEST')
            break
          case 'UNKNOWN_ERROR':
            props.change('coords_lat', '')
            props.change('coords_lng', '')
            props.change('googleMapGeocoding', '')
            setError('BBO_2858_UNKNOWN_ERROR')
            break
        }
      },
    )
  }

  // ${street},+${city},+${postCode},+${countryName}
  const geocoding = () => {
    if (mapsRef.current) {
      geocode(mapsRef.current)
      return
    }

    if (!bootstrap) {
      setBootstrap(true)
    }
  }

  const Marker: any = () => (
    <div
      style={{
        alignItems: 'center',
        backgroundColor: theme.colors.palette.darkGreyTwo,
        borderRadius: '50%',
        display: 'flex',
        height: '36px',
        justifyContent: 'center',
        width: '36px',
      }}
    >
      <FiHome style={{ color: 'white', fontSize: '20px' }} />
    </div>
  )
  console.log('isAllInOneFreeUser',isAllInOneFreeUser)
  return (
    <FormGroup.Render size={size} title={'Google Maps'} column={true}   withPopoverIcon={!isUserLicenceValid && !isAllInOneFreeUser}>
      <Flex
        column={!isDesktop}
        row={isDesktop}
        style={{ marginTop: `${15 * size}px` }}
      >
        <TextInput.ReduxField
          size={size}
          errorMsg={error}
          placeholder={'lat,lng'}
          onBlurCustom={onBlur}
          name={'googleMapGeocoding'}
          className="room-input-name"
          disabled={!isUserLicenceValid && !isAllInOneFreeUser}
          withPopoverIcon={!isUserLicenceValid && !isAllInOneFreeUser}
        />
        <Button
          onClick={isUserLicenceValid || isAllInOneFreeUser ? geocoding : isMobile? () => setIsModalOpened(true) : handlePopoverOpen}
          disabled={(!isUserLicenceValid && !isAllInOneFreeUser) || (!country || !street || !city || !postCode)}
          style={{
            marginLeft: isDesktop ? '10px' : '0px',
            marginTop: isDesktop ? '0px' : '10px',
            maxHeight: '36px',
            display: 'flex',
            gap: '4px',
            alignItems: 'center',
          }}
          primary={true}
          size={size}
        >
          {isUserLicenceValid || isAllInOneFreeUser ? (
            <FiMapPin />
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="10"
              height="13"
              viewBox="0 0 10 13"
              fill="none"
            >
              <path
                d="M8.80469 4.5H8.23326V3.35714C8.23326 1.78 6.95326 0.5 5.37612 0.5C3.79897 0.5 2.51897 1.78 2.51897 3.35714V4.5H1.94754C1.31897 4.5 0.804688 5.01429 0.804688 5.64286V11.3571C0.804688 11.9857 1.31897 12.5 1.94754 12.5H8.80469C9.43326 12.5 9.94754 11.9857 9.94754 11.3571V5.64286C9.94754 5.01429 9.43326 4.5 8.80469 4.5ZM5.37612 9.64286C4.74754 9.64286 4.23326 9.12857 4.23326 8.5C4.23326 7.87143 4.74754 7.35714 5.37612 7.35714C6.00469 7.35714 6.51897 7.87143 6.51897 8.5C6.51897 9.12857 6.00469 9.64286 5.37612 9.64286ZM7.14754 4.5H3.60469V3.35714C3.60469 2.38 4.39897 1.58571 5.37612 1.58571C6.35326 1.58571 7.14754 2.38 7.14754 3.35714V4.5Z"
                fill="white"
              />
            </svg>
          )}
          <p>{t('BBO_2858_geocoding')}</p>
        </Button>
        <PopoveredIcon2
          withIcon={false}
          anchorEl={popoverAnchor}
          onClose={handlePopoverClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
        />
        {isMobile && <UpgradePremiumBlockScreenModal
          open={isModalOpened}
          close={() => setIsModalOpened(false)}
        />}
      </Flex>
      {((lat && lng) || bootstrap) && (
        <div style={{ height: '300px' }}>
          <GoogleMapReact
            bootstrapURLKeys={{
              language: userLang,
              key: GEOCODING_API_KEY,
            }}
            options={{
              fullscreenControl: false,
              mapTypeControl: false,
              rotateControl: false,
              scaleControl: false,
              streetViewControl: false,
            }}
            defaultZoom={16}
            center={{ lat: Number(lat) || 0, lng: Number(lng) || 0 }}
            yesIWantToUseGoogleMapApiInternals={true}
            onGoogleApiLoaded={({ map, maps }) => {
              if (!mapsRef.current && bootstrap) {
                geocode(maps)
              }

              mapRef.current = map
              mapsRef.current = maps
            }}
          >
            <Marker lat={Number(lat) || 0} lng={Number(lng) || 0} />
          </GoogleMapReact>
        </div>
      )}
    </FormGroup.Render>
  )
})

export default RoomEditor

export interface IRoomEditorProps {
  id?: number
  roomId?: any
  initialValues?: IFormValues
  positions?: number[]
  colors?: TypeColor[]
  type?: 'update' | 'create'
  booking?: boolean
  airbnb?: boolean
  iCalendar?: boolean
  iCalendarSync?: number
  newRoom?: boolean
  syncFits?: boolean | string
}
